import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Row, Col, Statistic, Table, Space, Button, DatePicker, Segmented, Tag, message, Tooltip,
} from 'antd';
import {
  ShoppingOutlined, ShoppingCartOutlined, EuroOutlined,
  DownloadOutlined, ReloadOutlined, LinkOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { watchesAPI, apiClient } from 'Utils/api';
import { useNavigate } from 'react-router-dom';

type RangeKey = 'last_30_days' | 'current_month' | 'last_month' | 'last_quarter' | 'current_year' | 'custom';

const RANGE_OPTIONS: Array<{ value: RangeKey; label: string }> = [
  { value: 'last_30_days', label: 'Letzte 30 Tage' },
  { value: 'current_month', label: 'Aktueller Monat' },
  { value: 'last_month', label: 'Letzter Monat' },
  { value: 'last_quarter', label: 'Letztes Quartal' },
  { value: 'current_year', label: 'Aktuelles Jahr' },
  { value: 'custom', label: 'Zeitraum…' },
];

interface SaleRow {
  watchId: string;
  watchSlug: string;
  watchBrand: string;
  watchModel: string;
  watchReferenceNumber: string | null;
  watchShortCode: string | null;
  soldAt: string | null;
  unitPriceEur: number;
  grossEur: number;
  netEur: number;
  taxEur: number;
  taxRatePermille: number;
  taxScheme: string | null;
  orderId: string | null;
  orderNumber: string | null;
  orderStatus: string | null;
  paymentStatus: string | null;
  paymentMethod: string | null;
  customerName: string | null;
  customerEmail: string | null;
  countryCode: string | null;
}

interface SalesTotals {
  count: number;
  grossEur: number;
  netEur: number;
  taxEur: number;
}

const API_BASE = process.env.REACT_APP_API_URL || '';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ watches: 0, categories: 7 });
  const [statsLoading, setStatsLoading] = useState(true);

  const [range, setRange] = useState<RangeKey>('last_30_days');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [sales, setSales] = useState<SaleRow[]>([]);
  const [totals, setTotals] = useState<SalesTotals>({ count: 0, grossEur: 0, netEur: 0, taxEur: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [salesLoading, setSalesLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const queryParams = useMemo(() => {
    const p: Record<string, string> = { range };
    if (range === 'custom' && customRange) {
      p.from = customRange[0].format('YYYY-MM-DD');
      p.to = customRange[1].format('YYYY-MM-DD');
    }
    return p;
  }, [range, customRange]);

  const periodSummary = useMemo(() => {
    const pageNet = sales.reduce((acc, s) => acc + Number(s.netEur || 0), 0);
    const pageTax = sales.reduce((acc, s) => acc + Number(s.taxEur || 0), 0);
    const pageGross = sales.reduce((acc, s) => acc + Number(s.grossEur || 0), 0);
    return { pageNet, pageTax, pageGross };
  }, [sales]);

  useEffect(() => {
    (async () => {
      try {
        const response = await watchesAPI.getAll();
        const watches = response.data.watches || [];
        setStats((prev) => ({ ...prev, watches: watches.length }));
      } catch (e) {
        console.error('Stats laden fehlgeschlagen', e);
      } finally {
        setStatsLoading(false);
      }
    })();
  }, []);

  const fetchSales = async (overridePage?: number) => {
    setSalesLoading(true);
    try {
      const params = { ...queryParams, page: String(overridePage ?? page), pageSize: String(pageSize) };
      const res = await apiClient.get(`${API_BASE}/api/admin/sales`, { params });
      if (res.data?.status === 'success') {
        setSales(res.data.sales || []);
        setTotal(res.data.pagination?.total || 0);
        if (res.data.totals) setTotals(res.data.totals);
      }
    } catch (e) {
      message.error('Verkäufe konnten nicht geladen werden');
    } finally {
      setSalesLoading(false);
    }
  };

  useEffect(() => {
    if (range === 'custom' && !customRange) return;
    setPage(1);
    fetchSales(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, customRange]);

  useEffect(() => {
    if (range === 'custom' && !customRange) return;
    fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get(`${API_BASE}/api/admin/sales/export`, { params: queryParams });
      const { csv, filename } = res.data;
      if (!csv) {
        message.warning('Keine Daten zum Exportieren im gewählten Zeitraum');
        return;
      }
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'verkaeufe.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('CSV heruntergeladen');
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (iso: string | null) => iso ? dayjs(iso).format('DD.MM.YYYY') : '—';
  const formatAmount = (v: number) => Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const columns = [
    {
      title: 'Verkaufsdatum',
      dataIndex: 'soldAt',
      key: 'soldAt',
      width: 130,
      render: formatDate,
    },
    {
      title: 'Produkt',
      key: 'product',
      render: (_: any, r: SaleRow) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.watchBrand} {r.watchModel}</div>
          {r.watchReferenceNumber && <div style={{ color: '#888', fontSize: 12 }}>Ref. {r.watchReferenceNumber}</div>}
        </div>
      ),
    },
    {
      title: 'Bestellung',
      key: 'order',
      width: 180,
      render: (_: any, r: SaleRow) => r.orderNumber ? (
        <Tag color="blue" data-testid={`sale-order-${r.orderNumber}`}>{r.orderNumber}</Tag>
      ) : (
        <Tag color="default">— Direktverkauf —</Tag>
      ),
    },
    { title: 'Kunde', dataIndex: 'customerName', key: 'customerName', render: (v: string | null) => v || '—' },
    { title: 'E-Mail', dataIndex: 'customerEmail', key: 'customerEmail', render: (v: string | null) => v || '—' },
    {
      title: 'Brutto',
      dataIndex: 'grossEur',
      key: 'grossEur',
      width: 120,
      align: 'right' as const,
      render: (v: number) => <strong>{formatAmount(v)} €</strong>,
    },
    {
      title: 'Netto',
      dataIndex: 'netEur',
      key: 'netEur',
      width: 120,
      align: 'right' as const,
      render: (v: number) => <span style={{ color: '#3f8600' }}>{formatAmount(v)} €</span>,
    },
    {
      title: 'Steuer',
      key: 'tax',
      width: 150,
      align: 'right' as const,
      render: (_: any, r: SaleRow) => (
        <div>
          <div style={{ color: '#cf8a00' }}>{formatAmount(r.taxEur)} €</div>
          <div style={{ color: '#888', fontSize: 12 }}>
            {r.taxScheme === 'STANDARD_VAT_19'
              ? `USt. ${(r.taxRatePermille / 10).toFixed(0)}%`
              : '§25a (Marge)'}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_: any, r: SaleRow) => {
        if (!r.orderStatus) return <Tag>VERKAUFT</Tag>;
        return <Tag color={r.paymentStatus === 'PAID' ? 'green' : 'gold'}>{r.paymentStatus || r.orderStatus}</Tag>;
      },
    },
    {
      title: 'Aktion',
      key: 'action',
      width: 90,
      render: (_: any, r: SaleRow) => (
        <Tooltip title="Zum Produkt">
          <Button
            size="small"
            type="link"
            icon={<LinkOutlined />}
            onClick={() => navigate(`/products/${r.watchId}`)}
            data-testid={`sale-goto-watch-${r.watchId}`}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <h1>Dashboard</h1>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Produkte" value={stats.watches} prefix={<ShoppingOutlined />} loading={statsLoading} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Verkäufe (Zeitraum)" value={totals.count} prefix={<ShoppingCartOutlined />} loading={salesLoading} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card data-testid="stat-net-revenue">
            <Statistic
              title="Verkaufs-Netto (Zeitraum)"
              value={totals.netEur}
              prefix={<EuroOutlined />}
              precision={2}
              loading={salesLoading}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card data-testid="stat-tax">
            <Statistic
              title="Steuer (Zeitraum)"
              value={totals.taxEur}
              prefix={<EuroOutlined />}
              precision={2}
              loading={salesLoading}
              valueStyle={{ color: '#cf8a00' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Verkäufe"
        extra={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => fetchSales()} loading={salesLoading}>
              Aktualisieren
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
              data-testid="sales-export-csv"
            >
              CSV-Export
            </Button>
          </Space>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Segmented<RangeKey>
            value={range}
            onChange={(v) => setRange(v as RangeKey)}
            options={RANGE_OPTIONS}
            data-testid="sales-range-segmented"
          />
          {range === 'custom' && (
            <DatePicker.RangePicker
              value={customRange as any}
              onChange={(v) => setCustomRange((v as [Dayjs, Dayjs]) || null)}
              format="DD.MM.YYYY"
              data-testid="sales-range-custom"
            />
          )}
        </Space>

        <Table
          rowKey="watchId"
          dataSource={sales}
          columns={columns}
          loading={salesLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (t) => `Gesamt: ${t} Verkäufe`,
            onChange: (p, s) => {
              setPage(p);
              if (s !== pageSize) setPageSize(s);
            },
          }}
          size="middle"
          scroll={{ x: 'max-content' }}
          summary={(pageData) => {
            if (!pageData || pageData.length === 0) return null;
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    <strong>Summe (Seite)</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <strong data-testid="sum-gross">{formatAmount(periodSummary.pageGross)} €</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <strong style={{ color: '#3f8600' }} data-testid="sum-net">{formatAmount(periodSummary.pageNet)} €</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    <strong style={{ color: '#cf8a00' }} data-testid="sum-tax">{formatAmount(periodSummary.pageTax)} €</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} colSpan={2} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>
    </div>
  );
};

export default DashboardPage;
